import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SectionHeading from "@/components/feature/SectionHeading";
import MediaCard from "@/components/feature/MediaCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntry from "@/pages/home/components/CategoryEntry";
import NewsHighlight from "@/pages/home/components/NewsHighlight";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaList } from "@/mocks/media";
import { sortMedia, formatPopularity } from "@/utils/media";

export default function Home() {
  usePageMeta({
    title: "聚会的目的在线 - 电影电视剧资源大全",
    description:
      "聚会的目的在线为您提供丰富的电影和电视剧资源，涵盖剧情介绍、演员表与影视资讯，支持多类型影视内容浏览和选择。",
    keywords: "聚会的目的在线,电影,电视剧,影视资源,剧情介绍,聚会的目的电影,在线视频",
  });

  const latest = sortMedia(mediaList, "latest").slice(0, 8);
  const hot = sortMedia(mediaList, "popularity").slice(0, 5);
  const topRated = sortMedia(mediaList, "rating").slice(0, 4);

  return (
    <Layout>
      <HeroCarousel />
      <CategoryEntry />

      {/* Latest updates */}
      <section className="w-full px-4 md:px-8 mt-14 md:mt-20">
        <SectionHeading
          icon="ri-time-line"
          title="最新影视更新"
          subtitle="刚刚上线与持续更新的作品，第一时间了解"
          moreTo="/category/movie"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {latest.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* Hot recommend */}
      <section className="w-full px-4 md:px-8 mt-14 md:mt-20">
        <SectionHeading
          icon="ri-fire-line"
          title="热门影视推荐"
          subtitle="播放量与口碑双高的高人气作品"
          moreTo="/category/tv"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-5">
            {hot.map((item, i) => (
              <MediaCard key={item.id} item={item} rank={i + 1} />
            ))}
          </div>

          {/* Rating leaderboard */}
          <div className="rounded-lg border border-background-200 overflow-hidden h-fit">
            <div className="px-5 py-4 bg-primary-500 text-background-50 flex items-center gap-2">
              <i className="ri-trophy-line text-lg"></i>
              <h3 className="font-heading font-semibold text-base">口碑榜单</h3>
            </div>
            <div className="divide-y divide-background-200">
              {topRated.map((item, i) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex items-center gap-3 px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span
                    className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${
                      i === 0
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {item.type === "movie" ? "电影" : "电视剧"} · {item.year}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-accent-600">
                    {item.rating.toFixed(1)}
                  </span>
                </Link>
              ))}
            </div>
            <div className="px-5 py-4 bg-background-100 border-t border-background-200">
              <p className="text-xs text-foreground-500 flex items-center gap-1.5">
                <i className="ri-bar-chart-2-line text-primary-500"></i>
                本月热度最高作品已收获 {formatPopularity(mediaList[8].popularity)} 次浏览
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* News */}
      <section className="w-full px-4 md:px-8 mt-14 md:mt-20">
        <SectionHeading
          icon="ri-newspaper-line"
          title="影视资讯精选"
          subtitle="行业动态、剧集口碑与创作趋势"
          moreTo="/news"
        />
        <NewsHighlight />
      </section>

      {/* Intro */}
      <section className="w-full px-4 md:px-8 mt-14 md:mt-20">
        <div className="rounded-lg bg-primary-900 text-background-100 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 md:p-10">
            <div className="lg:col-span-2">
              <h2 className="font-heading font-semibold text-xl md:text-2xl text-white">
                关于聚会的目的在线
              </h2>
              <p className="mt-3 text-sm md:text-base text-primary-100 leading-relaxed max-w-2xl">
                我们是一个围绕电影与电视剧的信息门户，提供分类浏览、精准筛选、作品详情与影视资讯等服务。
                无论你想快速了解一部剧的剧情与演员，还是想追踪行业最新动态，这里都能帮你一站式完成。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category/movie"
                  className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center gap-2 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览影视库
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/about"
                  className="h-10 px-5 rounded-md border border-primary-700 text-background-100 text-sm font-medium flex items-center gap-2 hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-4 lg:pl-6 lg:border-l lg:border-primary-800">
              {[
                { value: "15", label: "精选作品" },
                { value: "6", label: "资讯专题" },
                { value: "12", label: "演员资料" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-heading font-bold text-2xl md:text-3xl text-accent-400">
                    {stat.value}
                  </p>
                  <p className="text-xs md:text-sm text-primary-200 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}